import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { adminApi } from '@/api';
import {
  Alert,
  Badge,
  BoxAvatar,
  BoxList,
  BoxRow,
  Button,
  CardActions,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Icon,
  LinkButton,
  ListCard,
  LoadingState,
  PageHeader,
  SegmentedTabs,
  StatTile,
  ViewButton,
  useToast,
} from '@/components/ui';
import { PlanFormModal } from '@/features/billing/PlanFormModal';
import {
  formatMoney,
  formatPrice,
  isStudentPlan,
  planGrantSummary,
} from '@/features/billing/money';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';

/**
 * The plan catalogue.
 *
 * One box per plan, in the same boxed-row style as every other list, with the
 * price always the first chip so the plans still compare at a glance.
 *
 * Plans are edited rather than deleted. Retiring one (`is_active`) takes it off
 * the menu and leaves everybody already paying for it exactly where they are;
 * hiding one (`is_public`) keeps it working while removing it from the pricing
 * page, which is how a negotiated price exists at all. Retiring a plan somebody
 * is on asks first, because the number of people affected is the point.
 */
/**
 * Who a plan is sold to. Companies and students are shown apart: they are
 * different customers, priced in different currencies, so their figures
 * cannot be added together. (An "All" tab was here; its money figures could
 * only ever say "—".)
 */
const AUDIENCE_TABS = [
  { key: 'COMPANY', label: 'Company plans' },
  { key: 'STUDENT', label: 'Student plans' },
];

/** A price per month, so a yearly plan can be compared with a monthly one. */
function perMonth(plan) {
  return plan.interval === 'YEARLY' ? plan.price_cents / 12 : plan.price_cents;
}

/** The stripe colour: what is wrong first, then whether it is on sale. */
function planAccent(plan) {
  if (plan.entitlements_drifted) return 'danger';
  if (!plan.is_active || plan.is_free_plan) return 'neutral';
  if (!plan.is_public) return 'warning';
  return 'success';
}

export function AdminPlansPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  // `null` closes the editor; `undefined` opens it for a new plan, so the two
  // are deliberately distinct states rather than a boolean plus a value.
  const [editing, setEditing] = useState(null);
  const [retiring, setRetiring] = useState(null);
  const [audience, setAudience] = useState('COMPANY');

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['admin-plans'],
    queryFn: adminApi.listBillingPlans,
  });

  const save = useMutation({
    mutationFn: ({ code, values }) => adminApi.updateBillingPlan(code, values),
    onSuccess: (_result, variables) => {
      toast.success(variables.message);
      setRetiring(null);
      ['admin-plan', 'admin-plans', 'admin-billing-overview', 'public-plans'].forEach(
        (key) => void queryClient.invalidateQueries({ queryKey: [key] }),
      );
    },
    onError: (saveError) => toast.error(errorMessage(saveError, 'Could not update the plan')),
  });

  if (isPending) return <LoadingState label="Loading plans…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  const catalogue = data ?? [];
  const plans = catalogue.filter((plan) => (plan.audience ?? 'COMPANY') === audience);
  const who = audience === 'STUDENT' ? 'students' : 'companies';
  // The free plan is active and public so the pricing page can print it, but
  // nobody buys it — counting it as "on sale" would contradict its own badge.
  const onSale = plans.filter((plan) => plan.is_active && plan.is_public && !plan.is_free_plan);
  const paid = onSale.filter((plan) => plan.price_cents > 0);
  const subscribers = plans.reduce((total, plan) => total + plan.subscriber_count, 0);
  const mrr = plans.reduce((total, plan) => total + plan.mrr_cents, 0);
  const currency = plans[0]?.currency ?? (audience === 'STUDENT' ? 'INR' : 'EUR');
  const drifted = catalogue.find((plan) => plan.entitlements_drifted);
  // One audience is normally one currency; a plan priced in another one is
  // possible, and then a sum would add two currencies together.
  const mixedCurrencies = new Set(plans.map((plan) => plan.currency)).size > 1;
  const cheapest = paid
    .filter((plan) => plan.currency === currency)
    .reduce((best, plan) => (!best || perMonth(plan) < perMonth(best) ? plan : best), null);

  return (
    <div>
      <PageHeader
        title="Plans"
        description="The catalogue companies and students buy from, and what each plan promises."
        action={
          <div className="flex flex-wrap items-center gap-2">
            <LinkButton to={paths.admin.subscriptions} variant="secondary" size="sm">
              Subscribers
            </LinkButton>
            <Button size="sm" onClick={() => setEditing(undefined)}>
              New plan
            </Button>
          </div>
        }
      />

      <SegmentedTabs
        label="Sold to"
        className="mb-6"
        tabs={AUDIENCE_TABS}
        value={audience}
        onChange={setAudience}
      />

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="On sale"
          value={onSale.length}
          hint={`${plans.length} ${audience === 'STUDENT' ? 'student' : 'company'} ${plans.length === 1 ? 'plan' : 'plans'} in the catalogue`}
        />
        <StatTile
          label="Subscribers"
          value={subscribers}
          hint={`${who[0].toUpperCase()}${who.slice(1)} on a running paid plan`}
        />
        <StatTile
          label="Monthly recurring"
          value={mixedCurrencies ? '—' : formatMoney(mrr, currency)}
          hint={
            mixedCurrencies
              ? 'These plans are priced in more than one currency'
              : 'Yearly plans divided down to a month'
          }
        />
        <StatTile
          label="Cheapest paid plan"
          value={
            cheapest ? formatPrice(cheapest.price_cents, cheapest.currency, cheapest.interval) : '—'
          }
          hint={cheapest ? `${cheapest.name} · the step up from free` : 'No paid plan on sale'}
        />
      </div>

      {drifted && (
        <Alert
          tone="warning"
          title="The free plan's row no longer matches what free grants"
          className="mb-6"
        >
          The platform enforces its own built-in free limits; this row is only what the pricing page
          prints.{' '}
          <Link to={paths.admin.planDetail(drifted.code)} className="underline">
            Compare the two
          </Link>{' '}
          and edit it back into agreement, or the site is promising something no code honours.
        </Alert>
      )}

      <ListCard
        toolbarStart={
          <span className="text-ink text-sm font-semibold">
            {audience === 'STUDENT' ? 'Student plans' : 'Company plans'}
          </span>
        }
        toolbarEnd={
          plans.length > 0 && (
            <span className="text-ink-muted text-sm">
              {plans.length} {plans.length === 1 ? 'plan' : 'plans'}
            </span>
          )
        }
        state={
          plans.length === 0 && audience ? (
            <EmptyState
              title={audience === 'STUDENT' ? 'No student plans yet' : 'No company plans yet'}
              description="Create one with New plan and choose who it is sold to."
              action={<Button onClick={() => setEditing(undefined)}>New plan</Button>}
            />
          ) : plans.length === 0 ? (
            <EmptyState
              title="No plans yet"
              description="Create one and it appears on the public pricing page as soon as it is active and public."
              action={<Button onClick={() => setEditing(undefined)}>New plan</Button>}
            />
          ) : null
        }
      >
        <BoxList>
          {plans.map((plan) => (
            <BoxRow
              key={plan.code}
              accent={planAccent(plan)}
              leading={<BoxAvatar icon="package" tone={plan.is_active ? 'brand' : 'neutral'} />}
              title={plan.name}
              to={paths.admin.planDetail(plan.code)}
              badges={
                <>
                  <Badge tone={isStudentPlan(plan) ? 'brand' : 'neutral'}>
                    {isStudentPlan(plan) ? 'Students' : 'Companies'}
                  </Badge>
                  {plan.is_free_plan ? (
                    <Badge tone="neutral">Not sold</Badge>
                  ) : plan.is_active && plan.is_public ? (
                    <Badge dot tone="success">
                      On sale
                    </Badge>
                  ) : null}
                  {!plan.is_public && <Badge tone="warning">Private</Badge>}
                  {!plan.is_active && <Badge>Retired</Badge>}
                  {plan.entitlements_drifted && <Badge tone="danger">Out of step</Badge>}
                </>
              }
              subtitle={<span className="font-mono text-xs">{plan.code}</span>}
              facts={[
                {
                  icon: 'banknote',
                  tone: 'brand',
                  text: plan.is_free_plan
                    ? 'No charge'
                    : `${formatMoney(plan.price_cents, plan.currency)} ${
                        plan.interval === 'YEARLY' ? 'a year' : 'a month'
                      }`,
                },
                { icon: 'sparkles', text: planGrantSummary(plan) },
                !isStudentPlan(plan) &&
                  plan.entitlements.candidate_sourcing && { icon: 'userSearch', text: 'Sourcing' },
                !isStudentPlan(plan) &&
                  plan.entitlements.ats_integration && { icon: 'plug', text: 'ATS' },
                isStudentPlan(plan) &&
                  plan.student_entitlements?.application_boost && {
                    icon: 'star',
                    text: 'Premium badge',
                  },
                {
                  icon: 'users',
                  text:
                    plan.subscriber_count > 0 ? (
                      <Link
                        to={`${paths.admin.subscriptions}?plan_id=${plan.id}${
                          isStudentPlan(plan) ? '&customer=student' : ''
                        }`}
                        className="hover:text-brand-700 hover:underline"
                        title={`Who is on ${plan.name}`}
                      >
                        {plan.subscriber_count}{' '}
                        {plan.subscriber_count === 1 ? 'subscriber' : 'subscribers'} · who is on it
                      </Link>
                    ) : (
                      '0 subscribers'
                    ),
                },
                {
                  icon: 'trendingUp',
                  text:
                    plan.mrr_cents > 0
                      ? `${formatMoney(plan.mrr_cents, plan.currency)} recurring a month`
                      : 'No recurring revenue',
                },
              ]}
              actions={
                <CardActions>
                  <Button
                    variant="secondary"
                    size="sm"
                    title={`Edit ${plan.name}`}
                    onClick={() => setEditing(plan)}
                  >
                    <Icon name="pencil" size={14} />
                    Edit
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    title={
                      plan.is_public
                        ? `Hide ${plan.name} from the pricing page`
                        : `Show ${plan.name} on the pricing page`
                    }
                    isLoading={save.isPending && save.variables?.code === plan.code}
                    onClick={() =>
                      save.mutate({
                        code: plan.code,
                        values: { is_public: !plan.is_public },
                        message: plan.is_public
                          ? `${plan.name} is hidden`
                          : `${plan.name} is shown on the pricing page`,
                      })
                    }
                  >
                    {!(save.isPending && save.variables?.code === plan.code) && (
                      <Icon name={plan.is_public ? 'eyeOff' : 'eye'} size={14} />
                    )}
                    {plan.is_public ? 'Hide plan' : 'Show plan'}
                  </Button>
                  <ViewButton to={paths.admin.planDetail(plan.code)} title={`Open ${plan.name}`} />
                </CardActions>
              }
              // The Retire button is switched off for now ("Hide plan" takes a
              // plan off the pricing page). Restore stays, so a plan retired
              // before can still come back. To bring Retire back, render this
              // in place of `null` below:
              //   <Button variant="ghostDanger" size="sm" title={`Retire ${plan.name}`}
              //     onClick={() => plan.subscriber_count > 0 ? setRetiring(plan)
              //       : save.mutate({ code: plan.code, values: { is_active: false },
              //           message: `${plan.name} is retired` })}>
              //     <Icon name="archive" size={14} /> Retire
              //   </Button>
              corner={
                plan.is_active ? null : (
                  <Button
                    variant="ghostPositive"
                    size="sm"
                    title={`Put ${plan.name} back on the menu`}
                    onClick={() =>
                      save.mutate({
                        code: plan.code,
                        values: { is_active: true },
                        message: `${plan.name} is back on the menu`,
                      })
                    }
                  >
                    <Icon name="restore" size={14} />
                    Restore
                  </Button>
                )
              }
            />
          ))}
        </BoxList>
      </ListCard>

      {/* Nobody subscribes to the free plan -- a company is on it by having no
          subscription at all -- so its row will always read zero, and a reader
          is owed the reason rather than left to wonder. */}
      <p className="text-ink-muted mt-3 text-xs">
        Ordered the way the pricing pages list them. Plans are retired, never deleted: a retired
        plan leaves the menu and everybody already on it keeps it. The free plans show no
        subscribers because nobody subscribes to them — being on free means having no paid plan.
      </p>

      <PlanFormModal open={editing !== null} plan={editing} onClose={() => setEditing(null)} />

      <ConfirmDialog
        open={retiring !== null}
        title={`Retire ${retiring?.name}`}
        message={`${retiring?.subscriber_count} ${
          isStudentPlan(retiring)
            ? retiring?.subscriber_count === 1
              ? 'student stays'
              : 'students stay'
            : retiring?.subscriber_count === 1
              ? 'company stays'
              : 'companies stay'
        } on it and keep everything it grants — retiring only takes it off the menu, so nobody new can subscribe. It can be restored at any time.`}
        confirmLabel="Retire plan"
        isLoading={save.isPending}
        onConfirm={() =>
          save.mutate({
            code: retiring.code,
            values: { is_active: false },
            message: `${retiring.name} is retired`,
          })
        }
        onCancel={() => setRetiring(null)}
      />
    </div>
  );
}
